// client/src/api.js
// API client for OrbitCal

const API_BASE = "/api";

function getToken() {
  return localStorage.getItem("orbitcal_token");
}

function setToken(token) {
  localStorage.setItem("orbitcal_token", token);
}

function clearToken() {
  localStorage.removeItem("orbitcal_token");
}

async function request(method, path, body = null) {
  const headers = { "Content-Type": "application/json" };
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const opts = { method, headers };
  if (body) opts.body = JSON.stringify(body);

  const res = await fetch(`${API_BASE}${path}`, opts);

  // Guard against non-JSON responses (rate-limit text, proxy error pages, etc.)
  let data = null;
  try { data = await res.json(); } catch { /* leave data null */ }

  if (!res.ok) {
    // Only treat 401 as an expired session when we actually sent a token and
    // weren't attempting to log in — otherwise a wrong password would reload
    // the page and swallow the error message.
    const isAuthAttempt = path === "/auth/login" || path === "/auth/register";
    if (res.status === 401 && token && !isAuthAttempt) {
      clearToken();
      window.location.reload();
    }
    throw new Error(data?.error || `Request failed (${res.status})`);
  }

  return data;
}

export const api = {
  // Auth
  register: (email, password, displayName) =>
    request("POST", "/auth/register", { email, password, displayName }),
  login: (email, password) =>
    request("POST", "/auth/login", { email, password }),
  logout: () => request("POST", "/auth/logout"),
  me: () => request("GET", "/auth/me"),
  forgotPassword: (email) => request("POST", "/auth/forgot-password", { email }),
  resetPassword: (token, password) => request("POST", "/auth/reset-password", { token, password }),

  // Organizations
  createOrg: (name) => request("POST", "/organizations", { name }),
  joinOrg: (inviteCode) => request("POST", "/organizations/join", { inviteCode }),
  getOrg: (orgId) => request("GET", `/organizations/${orgId}`),
  updateOrg: (orgId, data) => request("PUT", `/organizations/${orgId}`, data),

  // Members
  addMember: (data) => request("POST", "/members", data),
  updateMember: (memberId, data) => request("PUT", `/members/${memberId}`, data),
  deleteMember: (memberId) => request("DELETE", `/members/${memberId}`),
  refreshMember: (memberId) => request("POST", `/members/${memberId}/refresh`),

  // Events
  getEvents: (orgId, start, end) =>
    request("GET", `/events/${orgId}?start=${start.toISOString()}&end=${end.toISOString()}`),

  // Token management
  getToken,
  setToken,
  clearToken,
  isLoggedIn: () => !!getToken(),
};
